import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Manages the Horizon cart drawer, reservation timer and recommendation slider.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timerInterval = null;

  /** @type {HTMLElement | null} */
  #timerElement = null;

  /** @type {MutationObserver | null} */
  #recommendationsObserver = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    this.#recommendationsObserver = new MutationObserver(() => {
      const currentTimer = this.querySelector('#cart-timer');

      if (currentTimer !== this.#timerElement) {
        if (currentTimer instanceof HTMLElement) {
          this.#startCartTimer();
        } else {
          if (this.#timerInterval) clearInterval(this.#timerInterval);
          this.#timerInterval = null;
          this.#timerElement = null;
        }
      }

      if (
        this.#themeDrawer?.isOpen &&
        this.querySelector('[data-cart-recommendations]:not([data-loaded="true"]):not([data-loaded="loading"])')
      ) {
        this.#loadCartRecommendations();
      }
    });
    this.#recommendationsObserver.observe(this, {
      childList: true,
      subtree: true
    });

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);

    this.#recommendationsObserver?.disconnect();
    this.#recommendationsObserver = null;

    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = null;
    }
    this.#timerElement = null;
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#startCartTimer();
    this.#loadCartRecommendations();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this
        .querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#loadCartRecommendations();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-cart-recommendations-previous]')) {
      this.#moveRecommendations(-1);
      return;
    }

    if (event.target.closest('[data-cart-recommendations-next]')) {
      this.#moveRecommendations(1);
      return;
    }

    const dot = event.target.closest('[data-cart-recommendations-dot]');

    if (dot instanceof HTMLButtonElement) {
      this.#setRecommendationIndex(Number(dot.dataset.index || 0));
      return;
    }

    const addButton = event.target.closest(
      '[data-cart-recommendation-add]'
    );

    if (addButton instanceof HTMLButtonElement) {
      void this.#addRecommendation(addButton);
    }
  };

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.matches('[data-cart-recommendation-variant-select]')) return;

    const card = select.closest('[data-cart-recommendation-card]');
    const button = card?.querySelector('[data-cart-recommendation-add]');
    const option = select.options[select.selectedIndex];

    if (!(button instanceof HTMLButtonElement) || !option) return;

    button.dataset.variantId = option.value;
    button.disabled = option.dataset.available !== 'true';

    const price = card?.querySelector('[data-cart-recommendation-price]');
    const comparePrice = card?.querySelector(
      '[data-cart-recommendation-compare-price]'
    );

    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
    }

    if (comparePrice instanceof HTMLElement) {
      const value = option.dataset.comparePrice || '';
      comparePrice.textContent = value;
      comparePrice.hidden = !value;
    }
  };

  #loadCartRecommendations = async () => {
    const container = this.querySelector('[data-cart-recommendations]');

    if (!container) return;

    if (
      container.dataset.loaded === 'true' ||
      container.dataset.loaded === 'loading'
    ) {
      return;
    }

    const url = container.dataset.url;
    const content = container.querySelector(
      '.cart-recommendations__content'
    );

    if (!url || !content) return;

    container.dataset.loaded = 'loading';

    try {
      const response = await fetch(url, {
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Recommendations request failed: ${response.status}`
        );
      }

      const responseText = await response.text();
      const temporaryElement = document.createElement('div');
      temporaryElement.innerHTML = responseText;

      const recommendations = temporaryElement.querySelector(
        '[data-cart-recommendations-rendered]'
      );

      if (!recommendations || !recommendations.innerHTML.trim()) {
        container.remove();
        return;
      }

      content.innerHTML = recommendations.innerHTML;
      container.hidden = false;
      container.dataset.loaded = 'true';

      this.#initializeRecommendationsSlider();
      this.#updateStickyState();
    } catch (error) {
      console.error('[cart-drawer] Recommendations failed:', error);
      container.remove();
    }
  };

  #initializeRecommendationsSlider() {
    const slider = this.querySelector(
      '[data-cart-recommendations-slider]'
    );

    const cards = slider?.querySelectorAll(
      '[data-cart-recommendation-card]'
    );

    if (!slider || !cards?.length) return;

    slider.dataset.index = '0';

    const dots = this.querySelector(
      '[data-cart-recommendations-dots]'
    );

    if (dots) {
      dots.innerHTML = '';

      cards.forEach((_, index) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'cart-recommendations__dot';
        dot.dataset.cartRecommendationsDot = '';
        dot.dataset.index = String(index);
        dot.setAttribute('aria-label', `Visa produkt ${index + 1}`);

        dots.appendChild(dot);
      });
    }

    this.#renderRecommendationsSlider();
  }

  /**
   * @param {number} direction
   */
  #moveRecommendations(direction) {
    const slider = this.querySelector(
      '[data-cart-recommendations-slider]'
    );

    const cards = slider?.querySelectorAll(
      '[data-cart-recommendation-card]'
    );

    if (!slider || !cards?.length) return;

    const current = Number(slider.dataset.index || 0);
    const next = Math.min(
      Math.max(current + direction, 0),
      cards.length - 1
    );

    slider.dataset.index = String(next);
    this.#renderRecommendationsSlider();
  }

  /**
   * @param {number} index
   */
  #setRecommendationIndex(index) {
    const slider = this.querySelector(
      '[data-cart-recommendations-slider]'
    );

    const cards = slider?.querySelectorAll(
      '[data-cart-recommendation-card]'
    );

    if (!slider || !cards?.length) return;

    slider.dataset.index = String(
      Math.min(Math.max(index, 0), cards.length - 1)
    );

    this.#renderRecommendationsSlider();
  }

  #renderRecommendationsSlider() {
    const slider = this.querySelector(
      '[data-cart-recommendations-slider]'
    );

    const track = slider?.querySelector(
      '[data-cart-recommendations-track]'
    );

    const cards = slider?.querySelectorAll(
      '[data-cart-recommendation-card]'
    );

    if (
      !slider ||
      !(track instanceof HTMLElement) ||
      !cards?.length
    ) {
      return;
    }

    const index = Number(slider.dataset.index || 0);

    track.style.transform = `translateX(-${index * 100}%)`;

    const previous = slider.querySelector(
      '[data-cart-recommendations-previous]'
    );

    const next = slider.querySelector(
      '[data-cart-recommendations-next]'
    );

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = index === 0;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = index === cards.length - 1;
    }

    this.querySelectorAll(
      '[data-cart-recommendations-dot]'
    ).forEach((dot, dotIndex) => {
      dot.setAttribute(
        'aria-current',
        dotIndex === index ? 'true' : 'false'
      );
    });
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #addRecommendation = async (button) => {
    const variantId = Number(button.dataset.variantId);

    if (!variantId || button.disabled) return;

    const originalText = button.textContent || 'Lägg till';

    button.disabled = true;
    button.textContent = 'Lägger till...';

    try {
      const rootUrl = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ],
          sections: ['cart-drawer-section'],
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.description || 'Produkten kunde inte läggas till.'
        );
      }

      const sectionHtml =
        data.sections?.['cart-drawer-section'];

      if (!sectionHtml) {
        window.location.reload();
        return;
      }

      this.#replaceCartDrawer(sectionHtml);
    } catch (error) {
      console.error(
        '[cart-drawer] Add recommendation failed:',
        error
      );

      button.disabled = false;
      button.textContent = 'Försök igen';

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1600);
    }
  };

  /**
   * @param {string} sectionHtml
   */
  #replaceCartDrawer(sectionHtml) {
    const temporaryElement = document.createElement('div');
    temporaryElement.innerHTML = sectionHtml;

    const newSection = temporaryElement.querySelector(
      '#shopify-section-cart-drawer-section'
    );

    const currentSection = document.querySelector(
      '#shopify-section-cart-drawer-section'
    );

    if (!newSection || !currentSection) {
      window.location.reload();
      return;
    }

    currentSection.replaceWith(newSection);

    requestAnimationFrame(async () => {
      await customElements.whenDefined('theme-drawer');

      const newDrawer = document.querySelector('#cart-drawer');

      if (
        newDrawer &&
        typeof newDrawer.open === 'function'
      ) {
        newDrawer.open();
      }
    });
  }

  #startCartTimer() {
    const timer = this.querySelector('#cart-timer');

    if (!(timer instanceof HTMLElement)) return;

    if (this.#timerInterval) {
      clearInterval(this.#timerInterval);
    }
    this.#timerElement = timer;

    const storageKey = 'cart_timer_end';
    let endTime = Number(localStorage.getItem(storageKey));

    if (!endTime || endTime <= Date.now()) {
      endTime = Date.now() + 15 * 60 * 1000;
      localStorage.setItem(storageKey, String(endTime));
    }

    const update = () => {
      if (endTime <= Date.now()) {
        endTime = Date.now() + 15 * 60 * 1000;
        localStorage.setItem(storageKey, String(endTime));
      }

      const remaining = endTime - Date.now();
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      timer.textContent =
        `${String(minutes).padStart(2, '0')}:` +
        `${String(seconds).padStart(2, '0')}`;
    };

    update();
    this.#timerInterval = setInterval(update, 1000);
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
